export interface UserEquipment {
  userId: string;
  equipmentId: string;
  equipmentType: EquipmentType;
  brand?: string;
  model?: string;
  serialNumber?: string;
  purchaseDate?: string;
  notes?: string;
  diveCount: number;
  createdAt: string;
  updatedAt: string;
  isServiceable: boolean; // Whether this equipment requires service
  lastServiceDate?: string;
  serviceIntervalMonths?: number; // Service interval in months (e.g., 12 for annual)
  serviceIntervalDives?: number; // Legacy field - kept for backwards compatibility
  divesAtLastService: number;
  maintenanceNotes?: string; // JSON array of ServiceRecord
  isActive: boolean;
  isDefault: boolean; // If true, this equipment is automatically attached to new dives
}

export interface ServiceRecord {
  serviceDate: string;
  diveCountAtService: number;
  serviceNotes?: string;
  serviceType?: string;
}

export interface RecordServiceRequest {
  serviceDate?: string;
  serviceNotes?: string;
  serviceType?: string;
}

export type EquipmentType =
  | 'BCD'
  | 'Regulator'
  | 'Octo'
  | 'Wetsuit'
  | 'Drysuit'
  | 'Fins'
  | 'Mask'
  | 'Computer'
  | 'Tank'
  | 'WeightSystem'
  | 'Torch'
  | 'Camera'
  | 'Knife'
  | 'ReelSMB'
  | 'Other';

export const EQUIPMENT_TYPES: EquipmentType[] = [
  'BCD',
  'Regulator',
  'Octo',
  'Wetsuit',
  'Drysuit',
  'Fins',
  'Mask',
  'Computer',
  'Tank',
  'WeightSystem',
  'Torch',
  'Camera',
  'Knife',
  'ReelSMB',
  'Other'
];

export const EQUIPMENT_TYPE_LABELS: Record<EquipmentType, string> = {
  BCD: 'BCD',
  Regulator: 'Regulator',
  Octo: 'Octopus/Alternate Air Source',
  Wetsuit: 'Wetsuit',
  Drysuit: 'Drysuit',
  Fins: 'Fins',
  Mask: 'Mask',
  Computer: 'Dive Computer',
  Tank: 'Tank/Cylinder',
  WeightSystem: 'Weight System',
  Torch: 'Torch/Light',
  Camera: 'Camera',
  Knife: 'Knife/Cutting Tool',
  ReelSMB: 'Reel/SMB',
  Other: 'Other'
};

export const EQUIPMENT_TYPE_ICONS: Record<EquipmentType, string> = {
  BCD: '/equipment-icons/bcd.svg',
  Regulator: '/equipment-icons/regulator.svg',
  Octo: '/equipment-icons/octo.svg',
  Wetsuit: '/equipment-icons/wetsuit.svg',
  Drysuit: '/equipment-icons/drysuit.svg',
  Fins: '/equipment-icons/fins.svg',
  Mask: '/equipment-icons/mask.svg',
  Computer: '/equipment-icons/computer.svg',
  Tank: '/equipment-icons/tank.svg',
  WeightSystem: '/equipment-icons/weightsystem.svg',
  Torch: '/equipment-icons/torch.svg',
  Camera: '/equipment-icons/camera.svg',
  Knife: '/equipment-icons/knife.svg',
  ReelSMB: '/equipment-icons/reelsmb.svg',
  Other: '/equipment-icons/other.svg'
};

// Default service intervals (in months) based on industry standards
// null means no service interval recommended
export const EQUIPMENT_SERVICE_INTERVALS: Record<EquipmentType, number | null> = {
  BCD: 12,               // Critical life support - service annually (12 months)
  Regulator: 12,         // Critical life support - service annually (12 months)
  Octo: 12,              // Critical life support - service annually (12 months)
  Wetsuit: null,         // No scheduled service required
  Drysuit: 12,           // Annual seal inspection (12 months)
  Fins: null,            // No scheduled service required
  Mask: null,            // No scheduled service required
  Computer: 24,          // Battery and sensor service every 2 years (24 months)
  Tank: 12,              // Annual VIP (12 months) - hydrostatic test is separate
  WeightSystem: null,    // No scheduled service required
  Torch: 12,             // Annual O-ring check (12 months)
  Camera: 12,            // Annual O-ring check (12 months)
  Knife: null,           // No scheduled service required
  ReelSMB: null,         // No scheduled service required
  Other: null            // Unknown equipment type
};

// Whether equipment type typically requires service/maintenance
export const EQUIPMENT_REQUIRES_SERVICE: Record<EquipmentType, boolean> = {
  BCD: true,             // Critical - requires regular professional service
  Regulator: true,       // Critical - requires regular professional service
  Octo: true,            // Critical - requires regular professional service
  Wetsuit: false,        // Care-based maintenance only
  Drysuit: true,         // Requires seal and zipper maintenance
  Fins: false,           // Inspection only
  Mask: false,           // Inspection only
  Computer: true,        // Requires battery and sensor service
  Tank: true,            // Requires VIP and hydrostatic testing
  WeightSystem: false,   // Inspection only
  Torch: true,           // Requires O-ring maintenance
  Camera: true,          // Requires O-ring maintenance
  Knife: false,          // Care-based maintenance only
  ReelSMB: false,        // Inspection only
  Other: false           // Unknown - default to no service
};

export interface DefaultEquipment {
  userId: string;
  equipmentIds: string[];
  updatedAt: string;
}
